import React, { type ComponentProps, memo } from 'react';
import Animated from 'react-native-reanimated';

import { FocusScope } from '../FocusScope';
import { useAnimatedDialogAnimation } from './hooks';

export const AnimatedDialogView = memo((props: ViewContentProps) => {
  const { children, style, ...rest } = props;
  const animation = useAnimatedDialogAnimation({ show: true });

  return (
    <FocusScope>
      <Animated.View {...rest} style={[animation, style]}>
        {children}
      </Animated.View>
    </FocusScope>
  );
});

export const AnimatedDialogScrollView = memo((props: ContentProps) => {
  const { lockFocus, ...scrollProps } = props;

  return (
    <AnimatedDialogScrollViewWrapper lockFocus={lockFocus}>
      <AnimatedDialogScrollViewScroll {...scrollProps} />
    </AnimatedDialogScrollViewWrapper>
  );
});

export const AnimatedDialogScrollViewWrapper = memo(
  (props: Pick<ComponentProps<typeof FocusScope>, 'children' | 'lockFocus'>) => {
    const { children, lockFocus = true } = props;

    return (
      <FocusScope enabled lockFocus={lockFocus}>
        {children}
      </FocusScope>
    );
  },
);

export const AnimatedDialogScrollViewScroll = memo((props: ScrollContentProps) => {
  const { children, style, show = true, ...rest } = props;
  const animation = useAnimatedDialogAnimation({ show });

  return (
    <Animated.ScrollView {...rest} style={[animation, style]}>
      {children}
    </Animated.ScrollView>
  );
});

// ─── Types ──────────────────────────────────────────────────────────────────

type ViewContentProps = Omit<ComponentProps<typeof Animated.View>, 'ref'>;

type ScrollContentProps = Omit<ComponentProps<typeof Animated.ScrollView>, 'ref'> & {
  show?: boolean;
};

type ContentProps = ComponentProps<typeof AnimatedDialogScrollViewScroll> &
  Pick<ComponentProps<typeof AnimatedDialogScrollViewWrapper>, 'lockFocus'>;
